import React, { useEffect, useRef, useContext } from 'react';
import { CaseStudyColorContext } from '../pages/CaseStudy';
import './Carousel.css';

function CarouselItem({ item, isActive }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (item.type !== 'video' || !video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && isActive && item.autoplay) {
          video.play().catch(err => {
            console.error('Autoplay prevented:', err);
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [isActive, item.type, item.autoplay]);

  if (item.type === 'video') {
    return (
      <div className="carousel-item">
        <video
          ref={videoRef}
          src={item.video?.asset?.url || item.video?.url}
          poster={item.poster?.asset?.url || item.poster?.url}
          playsInline
          loop={item.autoplay}
          muted={item.autoplay}
          className="carousel-media"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
      </div>
    );
  }

  return (
    <div className="carousel-item">
      <img
        src={item.image?.asset?.url || item.image?.url}
        alt={item.alt}
        className="carousel-media"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      />
    </div>
  );
}

function Carousel({ module }) {
  const carouselRef = useRef(null);
  const autoplayIntervalRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const { background, accent } = useContext(CaseStudyColorContext);

  const items = module.items || [];
  const totalItems = items.length;

  const scrollLeft = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth;
      carouselRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Autoplay functionality
  useEffect(() => {
    if (module.autoplay && totalItems > 1 && carouselRef.current) {
      const interval = module.autoplayInterval || 5;
      autoplayIntervalRef.current = setInterval(() => {
        if (carouselRef.current) {
          const scrollAmount = carouselRef.current.clientWidth;
          carouselRef.current.scrollBy({
            left: scrollAmount,
            behavior: 'smooth'
          });
          
          // Reset to start if at the end
          setTimeout(() => {
            if (carouselRef.current) {
              const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
              if (scrollLeft + clientWidth >= scrollWidth - 10) {
                carouselRef.current.scrollTo({
                  left: 0,
                  behavior: 'smooth'
                });
              }
            }
          }, 500);
        }
      }, interval * 1000);

      return () => {
        if (autoplayIntervalRef.current) {
          clearInterval(autoplayIntervalRef.current);
        }
      };
    }
  }, [module.autoplay, module.autoplayInterval, totalItems]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Momentum scrolling animation
  const animateMomentum = () => {
    if (!carouselRef.current) return;
    
    const friction = 0.95; // Friction coefficient (0.95 = 5% reduction per frame)
    const minVelocity = 0.5; // Minimum velocity to continue
    
    if (Math.abs(velocityRef.current) > minVelocity) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const newScrollLeft = scrollLeft - velocityRef.current;
      
      // Check boundaries
      if (newScrollLeft <= 0) {
        carouselRef.current.scrollLeft = 0;
        velocityRef.current = 0;
        carouselRef.current.style.scrollBehavior = 'smooth';
        return;
      } else if (newScrollLeft >= scrollWidth - clientWidth) {
        carouselRef.current.scrollLeft = scrollWidth - clientWidth;
        velocityRef.current = 0;
        carouselRef.current.style.scrollBehavior = 'smooth';
        return;
      }
      
      carouselRef.current.scrollLeft = newScrollLeft;
      velocityRef.current *= friction; // Apply friction
      animationFrameRef.current = requestAnimationFrame(animateMomentum);
    } else {
      velocityRef.current = 0;
      carouselRef.current.style.scrollBehavior = 'smooth';
    }
  };

  // Global mouse handlers for smooth dragging outside container
  const handleGlobalMouseMove = (e) => {
    if (isDraggingRef.current && carouselRef.current) {
      e.preventDefault();
      const x = e.pageX;
      const now = Date.now();
      
      // Calculate velocity (pixels per millisecond)
      if (lastTimeRef.current > 0) {
        const deltaX = x - lastXRef.current;
        const deltaTime = now - lastTimeRef.current;
        if (deltaTime > 0) {
          velocityRef.current = deltaX / deltaTime * 16; // Convert to pixels per frame (assuming 60fps)
        }
      }
      
      lastXRef.current = x;
      lastTimeRef.current = now;
      
      const walk = x - startXRef.current; // Direct 1:1 mouse movement
      carouselRef.current.scrollLeft = scrollLeftRef.current - walk;
    }
  };

  const handleGlobalMouseUp = () => {
    if (carouselRef.current) {
      isDraggingRef.current = false;
      carouselRef.current.style.cursor = 'grab';
      carouselRef.current.style.userSelect = '';
      
      // Start momentum animation if there's velocity
      if (Math.abs(velocityRef.current) > 0.5) {
        carouselRef.current.style.scrollBehavior = 'auto';
        // Cancel any existing animation
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(animateMomentum);
      } else {
        carouselRef.current.style.scrollBehavior = 'smooth';
      }
    }
    document.removeEventListener('mousemove', handleGlobalMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  };

  // Drag to scroll handlers
  const handleMouseDown = (e) => {
    if (!carouselRef.current) return;
    
    // Cancel any existing momentum animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    isDraggingRef.current = true;
    startXRef.current = e.pageX;
    lastXRef.current = e.pageX;
    lastTimeRef.current = Date.now();
    scrollLeftRef.current = carouselRef.current.scrollLeft;
    velocityRef.current = 0;
    carouselRef.current.style.cursor = 'grabbing';
    carouselRef.current.style.userSelect = 'none';
    carouselRef.current.style.scrollBehavior = 'auto'; // Disable smooth scroll during drag
    e.preventDefault();
    
    // Add global listeners for smooth dragging outside container
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
  };

  // const handleMouseMove = (e) => {
  //   if (!isDraggingRef.current || !carouselRef.current) return;
  //   e.preventDefault();
  //   const x = e.pageX;
  //   const now = Date.now();
  //
  //   // Calculate velocity (pixels per millisecond)
  //   if (lastTimeRef.current > 0) {
  //     const deltaX = x - lastXRef.current;
  //     const deltaTime = now - lastTimeRef.current;
  //     if (deltaTime > 0) {
  //       velocityRef.current = deltaX / deltaTime * 16; // Convert to pixels per frame (assuming 60fps)
  //     }
  //   }
  //
  //   lastXRef.current = x;
  //   lastTimeRef.current = now;
  //
  //   const walk = x - startXRef.current; // Direct 1:1 mouse movement
  //   carouselRef.current.scrollLeft = scrollLeftRef.current - walk;
  // };

  // const handleMouseUp = () => {
  //   handleGlobalMouseUp();
  // };

  const handleMouseLeave = () => {
    // Don't stop dragging on mouse leave - let global handlers manage it
    // This allows smooth dragging even when mouse leaves the container
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!carouselRef.current) return;
    
    // Cancel any existing momentum animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    isDraggingRef.current = true;
    startXRef.current = e.touches[0].pageX;
    lastXRef.current = e.touches[0].pageX;
    lastTimeRef.current = Date.now();
    scrollLeftRef.current = carouselRef.current.scrollLeft;
    velocityRef.current = 0;
    carouselRef.current.style.scrollBehavior = 'auto'; // Disable smooth scroll during drag
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current || !carouselRef.current) return;
    const x = e.touches[0].pageX;
    const now = Date.now();
    
    // Calculate velocity (pixels per millisecond)
    if (lastTimeRef.current > 0) {
      const deltaX = x - lastXRef.current;
      const deltaTime = now - lastTimeRef.current;
      if (deltaTime > 0) {
        velocityRef.current = deltaX / deltaTime * 16; // Convert to pixels per frame
      }
    }
    
    lastXRef.current = x;
    lastTimeRef.current = now;
    
    const walk = x - startXRef.current; // Direct 1:1 touch movement
    carouselRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleTouchEnd = () => {
    if (!carouselRef.current) return;
    isDraggingRef.current = false;
    
    // Start momentum animation if there's velocity
    if (Math.abs(velocityRef.current) > 0.5) {
      carouselRef.current.style.scrollBehavior = 'auto';
      // Cancel any existing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(animateMomentum);
    } else {
      carouselRef.current.style.scrollBehavior = 'smooth';
    }
  };

  // Pause autoplay on hover/start
  const handleMouseEnter = () => {
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
    }
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div 
      className="carousel-module"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        ref={carouselRef}
        className="carousel-container"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: 'grab' }}
      >
        <div className="carousel-track">
          {items.map((item, index) => (
            <CarouselItem
              key={index}
              item={item}
              isActive={true}
            />
          ))}
        </div>
      </div>

      {module.showArrows && totalItems > 1 && (
        <>
          <button
            className="carousel-arrow carousel-arrow-left"
            onClick={scrollLeft}
            aria-label="Scroll left"
            style={{
              '--arrow-bg': background,
              '--arrow-color': accent
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button
            className="carousel-arrow carousel-arrow-right"
            onClick={scrollRight}
            aria-label="Scroll right"
            style={{
              '--arrow-bg': background,
              '--arrow-color': accent
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </>
      )}
    </div>
  );
}

export default Carousel;

